import React, { useEffect, useState } from "react";
import "./account.css";
import Box from "@mui/material/Box";
import { Grid, Button, Chip, DialogContent, Typography } from "@material-ui/core";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import * as Yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import useAxiosPrivate from "../../utils/hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import { setName, setLastName, setProfile, setEmail } from "../../store/actions/authActions";
import DropFileInput from "./dropImage";
import AccountModalAdmin from "./accountModalAdmin";
import Dialog from "@mui/material/Dialog";
import ModalCloseButton from "../../components/UI/ModalCloseButton";

const useStyles = makeStyles((theme) => ({
  chip: {
    fontFamily: "Poppins",
    backgroundColor: "#ECF9F7",
    border: "2px solid",
    borderColor: "#0F9BB0",
    fontWeight: "400",
    color: "#000000",
    fontSize: "0.8rem",
    margin: "0.4rem",
    padding: "0.5rem 0.5rem 0.5rem 0.5rem",
    lineHeight: "1rem",
  },
}));

let AccountSchema = Yup.object().shape({
  first_name: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .max(15, "First name must be at most 15 characters")
    .required("First Name is required!"),
  last_name: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .max(15, "Last name must be at most 15 characters")
    .required("Last Name is required!"),
});

export default function AccountModal(props) {
  const axios = useAxiosPrivate();
  const authState = useSelector((state) => state.auth);
  const role = authState.role;
  const classes = useStyles();
  const { setOpen, open, setOpenedPassword, setOpenAuthentication } = props;
  const [edit, setEdit] = useState(false);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    url: "",
  });
  const [teams, setTeams] = useState([]);
  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "success",
    message: "",
  });
  const dispatch = useDispatch();

  useEffect(() => {
    if (!open) hasFetched.current = false;
    if (open) {
      // Immediately start loading data from API (don't wait for Redux pre-population)
      // This ensures last_name loads as quickly as possible
      loadData();
    }
    // eslint-disable-next-line
  }, [open]);

  const onFileChange = async (files) => {
    loadData();
  };

  const hasFetched = React.useRef(false);

  const loadData = async (e) => {
    if (hasFetched.current) return;   // prevent double call
    hasFetched.current = true;

    try {
      // Pre-populate from Redux immediately for faster initial display
      if (authState.name || authState.email || authState.url) {
        setInitialValues({
          first_name: authState.name || "",
          last_name: authState.last_name || "", // Will be filled by API if not in Redux
          email: authState.email || "",
          password: "hiddenPassword",
          url: authState.url || "",
        });
        setEdit(true); // Set edit mode if we have data from Redux
      }
      
      // Always load fresh data from API to get complete profile
      console.log('from account modal : /user/profile ');
      const { data } = await axios.get("/user/profile");
      
      if (!data || !data.data) {
        throw new Error("Invalid response from server");
      }
      
      const profileData = data.data;
      const imageUrl = profileData.avatar_url || "";
      
      // Update form with complete data (including last_name)
      setInitialValues({
        first_name: profileData.first_name || "",
        last_name: profileData.last_name || "",
        email: profileData.email || "",
        password: "hiddenPassword",
        url: imageUrl,
      });
      
      // Update Redux store with profile data
      if (profileData.first_name) {
        dispatch(setName(profileData.first_name));
      }
      if (profileData.last_name) {
        dispatch(setLastName(profileData.last_name));
      }
      if (profileData.email) {
        dispatch(setEmail(profileData.email));
      }
      if (imageUrl) {
        dispatch(setProfile(imageUrl));
      }
      
      // Set teams if available
      if (profileData.groups && Array.isArray(profileData.groups)) {
        setTeams(profileData.groups);
      } else {
        setTeams([]);
      }
      
      setEdit(true);
    } catch (error) {
      console.error("Error loading profile data:", error);
      
      // If we have Redux data, keep it visible even if API fails
      if (!authState.name && !authState.email) {
        // Only show error if we don't have any fallback data
        if (error.response && error.response?.data) {
          setAlert({
            showAlert: true,
            severity: "error",
            message: error.response?.data?.message || "Failed to load profile data",
          });
        } else {
          setAlert({
            showAlert: true,
            severity: "error",
            message: "Failed to load profile data. Please try again.",
          });
        }
      }
    }
  };

  const submit = async (e) => {
    try {
      await axios.post("/user/update", {
        first_name: e.first_name,
        last_name: e.last_name,
      });
      dispatch(setName(e.first_name));
      setEdit(true);
      setAlert({
        showAlert: true,
        severity: "success",
        message: "User updated Successfully!",
      });
      setTimeout(() => {
        setAlert({
          showAlert: false,
          severity: "success",
          message: "User updated Successfully!",
        });
      }, 5000);
    } catch (error) {
      if (error.response && error.response?.data) {
        setAlert({
          showAlert: true,
          severity: "error",
          message: error.response?.data?.message,
        });
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          message: "Something was wrong!",
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <DialogContent style={{ margin: '0 2rem 0 2rem' }}>
        <Formik
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={AccountSchema}
          enableReinitialize={true}
        >
          {({ dirty, isValid }) => {
            return (
              <Form>
                <Grid container>
                  <Grid item xs={12} sm={12} md={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <ModalCloseButton
                        onClick={() => setOpen(false)}
                        style={{ position: "static" }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="subtitle2" style={{ fontSize: "20px", lineHeight: "30px" }}>Account</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", margin: '1.5rem 0' }}>
                      <Avatar
                        alt="user profile picture"
                        src={initialValues.url}
                        sx={{ width: 90, height: 90 }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", margin: '-1.5rem 0 1rem 0' }}>
                      <DropFileInput onFileChange={(files) => onFileChange(files)} />
                    </Box>
                  </Grid>
                  <Grid item md={12} container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={5} md={5}>
                      <Field
                        style={{
                          fontFamily: "Poppins",
                        }}
                        name="first_name"
                        label="First Name"
                        disabled={edit ? true : false}
                        component={TextField}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                      ></Field>
                    </Grid>
                    <Grid item xs={12} sm={5} md={5}>
                      <Field
                        style={{
                          fontFamily: "Poppins",
                        }}
                        name="last_name"
                        label="Last Name"
                        disabled={edit ? true : false}
                        component={TextField}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                      ></Field>
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                      {!edit &&
                        (isValid ? (
                          <Button
                            fullWidth
                            size="large"
                            type="submit"
                            style={{
                              fontSize: "0.9rem",
                              lineHeight: "1.25rem",
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              fontStyle: "normal",
                              width: 85,
                              height: 38,
                              backgroundColor: "#0F9BB0",
                              color: "white",
                              textTransform: "none",
                            }}
                          >
                            Save
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            size="large"
                            disabled={true}
                            style={{
                              fontSize: "0.9rem",
                              lineHeight: "1.25rem",
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              fontStyle: "normal",
                              opacity: 0.5,
                              width: 85,
                              height: 38,
                              backgroundColor: "#0F9BB0",
                              color: "white",
                              textTransform: "none",
                            }}
                          >
                            Save
                          </Button>
                        ))}
                      {edit && (
                        <Button
                          fullWidth
                          size="large"
                          type="none"
                          onClick={() => setEdit(false)}
                          style={{
                            fontSize: "0.9rem",
                            lineHeight: "1.25rem",
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            fontStyle: "normal",
                            width: 85,
                            height: 38,
                            backgroundColor: "white",
                            color: "#0F9BB0",
                            textTransform: "none",
                            border: "2px solid",
                            borderColor: "#0F9BB0",
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} >
                    <Typography variant="h2" style={{ fontSize: "0.8rem", marginBottom: "-0.5rem", lineHeight: "30px", color: "#0F9BB0", textDecoration: "underline", textAlign: "end", cursor: "pointer" }} onClick={() => setOpenAuthentication(true)}>{role === "COACH" ? "Request to change" : "Change"}</Typography>
                    <Field
                      style={{
                        fontFamily: "Poppins",
                      }}
                      name="email"
                      label="Email"
                      disabled
                      component={TextField}
                      variant="outlined"
                      fullWidth
                      margin="dense"
                    ></Field>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} >
                    <Typography variant="h2" style={{ fontSize: "0.8rem", marginBottom: "-0.5rem", lineHeight: "30px", color: "#0F9BB0", textDecoration: "underline", textAlign: "end", cursor: "pointer" }} onClick={() => setOpenedPassword(true)}>Change</Typography>
                    <Field
                      name="password"
                      label="Password"
                      component={TextField}
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      disabled
                      type={"password"}
                      style={{
                        fontFamily: "Poppins",
                      }}
                    ></Field>
                  </Grid>
                  {role === "COACH" ?
                    <Grid item xs={12} sm={12} md={12}>
                      <Typography variant="h3" style={{ fontSize: "1rem", lineHeight: "30px", textAlign: "start" }}>Teams</Typography>
                      <Box
                        style={{
                          width: "30.5rem",
                          height: "11rem",
                          overflowY: "auto",
                          overflowX: "none",
                        }}
                      >
                        {teams.map((item) => {
                          return (
                            <Chip
                              label={item.name}
                              className={classes.chip}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    </Grid> : <AccountModalAdmin />}
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  {alert.showAlert ? (
                    <Snackbar
                      open={alert.showAlert}
                      autoHideDuration={5000}
                      onClose={() =>
                        setAlert({
                          ...alert,
                          showAlert: false,
                        })
                      }
                    >
                      <Alert
                        severity={alert.severity}
                        onClose={() =>
                          setAlert({
                            ...alert,
                            showAlert: false,
                          })
                        }
                      >
                        {alert.message}
                      </Alert>
                    </Snackbar>
                  ) : null}
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
